import { useState } from "react";
import {
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Input,
  Spinner,
  Text,
} from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppMyAccountCard from "@/features/my-account/app-my-account-card";
import StatusBadge from "@/shared/components/StatusBadge";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import {
  BoutiqueBankInfo,
  BoutiqueBankInfoRequestBody,
  useDeleteBoutiqueBankInfo,
  useListBoutiqueBankInfo,
  useSaveBoutiqueBankInfo,
} from "@/features/my-account/hooks/use-boutique-account";

const EMPTY_FORM: BoutiqueBankInfoRequestBody = {
  alias: "",
  bankName: "",
  iban: "",
  swiftCode: "",
  branchName: "",
  branchAddress: "",
};

// 편집 중인 계좌 — id 가 없으면 새로 추가하는 중이다.
interface Editing {
  id?: number;
  values: BoutiqueBankInfoRequestBody;
}

const FIELDS: {
  name: keyof BoutiqueBankInfoRequestBody;
  label: string;
  placeholder: string;
  required?: boolean;
}[] = [
  {
    name: "bankName",
    label: "은행명",
    placeholder: "예: 국민은행",
    required: true,
  },
  {
    name: "iban",
    label: "계좌번호",
    placeholder: "예: 123-456-789012",
    required: true,
  },
  { name: "alias", label: "별칭", placeholder: "예: 원화 입금 계좌" },
  { name: "swiftCode", label: "SWIFT CODE", placeholder: "해외 송금용 (선택)" },
];

// 부티크 계좌 정보 — 오더 거래명세서의 입금계좌에는 가장 먼저 등록한 계좌가 찍힌다.
export default function BoutiqueBankInfoCard() {
  const { info } = useAppToasts({ id: "GLOBAL" });
  const { data: bankInfoList = [], isPending: isLoading } =
    useListBoutiqueBankInfo();
  const { mutateAsync: saveBankInfo, isPending: isSaving } =
    useSaveBoutiqueBankInfo();
  const { mutateAsync: deleteBankInfo, isPending: isDeleting } =
    useDeleteBoutiqueBankInfo();
  const [editing, setEditing] = useState<Editing | null>(null);
  // 삭제는 한 번 더 눌러야 실행된다 — 브라우저 confirm 대신 버튼 문구로 확인한다.
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const canSave =
    !!editing &&
    editing.values.bankName.trim() !== "" &&
    editing.values.iban.trim() !== "";

  const handleSave = async () => {
    if (!editing || !canSave) {
      return;
    }
    const body = Object.fromEntries(
      Object.entries(editing.values).map(([key, value]) => [key, value.trim()])
    ) as BoutiqueBankInfoRequestBody;
    try {
      await saveBankInfo({ id: editing.id, body });
      info("계좌 정보가 저장되었습니다.");
      setEditing(null);
    } catch {
      // 실패 토스트는 저장 훅의 onError가 띄운다.
    }
  };

  const handleDelete = async (bankInfo: BoutiqueBankInfo) => {
    if (deletingId !== bankInfo.id) {
      setDeletingId(bankInfo.id);
      return;
    }
    try {
      await deleteBankInfo(bankInfo.id);
      info("계좌 정보가 삭제되었습니다.");
    } catch {
      // 실패 토스트는 삭제 훅의 onError가 띄운다.
    } finally {
      setDeletingId(null);
    }
  };

  const editForm = editing && (
    <Flex
      flexDirection={"column"}
      gap={"12px"}
      padding={"16px"}
      borderRadius={"4px"}
      border={"0.5px solid #EFEFED"}
      background={"#F7F8FA"}
    >
      <Grid
        gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
        columnGap={"16px"}
        rowGap={"12px"}
      >
        {FIELDS.map((f) => (
          <FormControl key={f.name} isRequired={f.required}>
            <FormLabel display={"flex"} m={"0"}>
              <Text color={"#6F6F6F"} fontSize={"12px"} fontWeight={"400"}>
                {f.label}
              </Text>
            </FormLabel>
            <Input
              value={editing.values[f.name]}
              onChange={(event) =>
                setEditing({
                  ...editing,
                  values: { ...editing.values, [f.name]: event.target.value },
                })
              }
              fontSize={"12px"}
              minHeight={"0"}
              height={"28px"}
              background={"#FFF"}
              placeholder={f.placeholder}
            />
          </FormControl>
        ))}
      </Grid>
      <Flex justifyContent={"flex-end"} gap={"8px"}>
        <AppButton
          variant={"normal"}
          size={"small"}
          onClick={() => setEditing(null)}
          isDisabled={isSaving}
        >
          취소
        </AppButton>
        <AppButton
          variant={"primary"}
          size={"small"}
          onClick={handleSave}
          isLoading={isSaving}
          isDisabled={!canSave}
        >
          저장
        </AppButton>
      </Flex>
    </Flex>
  );

  return (
    <AppMyAccountCard title={"계좌 정보"}>
      <Flex flexDirection={"column"} gap={"12px"}>
        <Text fontSize={"12px"} color={"#6F6F6F"}>
          거래명세서의 입금계좌에는 가장 먼저 등록한 계좌가 표시됩니다.
        </Text>

        {isLoading ? (
          <Spinner size={"sm"} color={"#8F8F8C"} />
        ) : bankInfoList.length === 0 && !editing ? (
          <Text textStyle={"body-md-bold"} color={"#1D1D1B"}>
            등록된 계좌가 없습니다.
          </Text>
        ) : (
          bankInfoList.map((bankInfo, index) =>
            editing?.id === bankInfo.id ? (
              <Flex key={bankInfo.id} flexDirection={"column"}>
                {editForm}
              </Flex>
            ) : (
              <Flex
                key={bankInfo.id}
                alignItems={"center"}
                justifyContent={"space-between"}
                gap={"8px"}
                flexWrap={"wrap"}
                padding={"12px 16px"}
                borderRadius={"4px"}
                border={"0.5px solid #EFEFED"}
              >
                <Flex flexDirection={"column"} gap={"2px"} minWidth={0}>
                  <Flex alignItems={"center"} gap={"8px"}>
                    <Text fontSize={"13px"} fontWeight={"600"}>
                      {bankInfo.bankName} {bankInfo.iban}
                    </Text>
                    {index === 0 && (
                      <StatusBadge
                        status={"거래명세서 표시"}
                        colorVariant={"green"}
                      />
                    )}
                  </Flex>
                  {(bankInfo.alias || bankInfo.swiftCode) && (
                    <Text fontSize={"12px"} color={"#8F8F8C"}>
                      {[
                        bankInfo.alias,
                        bankInfo.swiftCode && `SWIFT ${bankInfo.swiftCode}`,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </Text>
                  )}
                </Flex>
                <Flex gap={"8px"}>
                  <AppButton
                    variant={"normal"}
                    size={"small"}
                    isDisabled={!!editing || isDeleting}
                    onClick={() => {
                      setDeletingId(null);
                      setEditing({
                        id: bankInfo.id,
                        values: {
                          alias: bankInfo.alias ?? "",
                          bankName: bankInfo.bankName ?? "",
                          iban: bankInfo.iban ?? "",
                          swiftCode: bankInfo.swiftCode ?? "",
                          branchName: bankInfo.branchName ?? "",
                          branchAddress: bankInfo.branchAddress ?? "",
                        },
                      });
                    }}
                  >
                    수정
                  </AppButton>
                  <AppButton
                    variant={"normal"}
                    size={"small"}
                    isDisabled={!!editing}
                    isLoading={isDeleting && deletingId === bankInfo.id}
                    onClick={() => handleDelete(bankInfo)}
                  >
                    {deletingId === bankInfo.id ? "삭제 확인" : "삭제"}
                  </AppButton>
                </Flex>
              </Flex>
            )
          )
        )}

        {editing && editing.id === undefined && editForm}

        {!editing && (
          <Flex justifyContent={"flex-end"}>
            <AppButton
              variant={"primary"}
              size={"medium"}
              onClick={() => {
                setDeletingId(null);
                setEditing({ values: EMPTY_FORM });
              }}
            >
              계좌 추가
            </AppButton>
          </Flex>
        )}
      </Flex>
    </AppMyAccountCard>
  );
}
